/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React from "react";
import { Globe, Landmark, ExternalLink, ChevronDown, ChevronUp, Check, MinusCircle, AlertTriangle, Search } from "lucide-react";

const CONF = {
  high: ["高", "High", "bg-emerald-100 text-emerald-700"],
  medium: ["中", "Med", "bg-amber-100 text-amber-700"],
  low: ["低", "Low", "bg-slate-100 text-slate-500"],
};
const SRC = { "洛杉矶县评估官": ["评估官", "Assessor", "bg-indigo-100 text-indigo-700"], "联网检索": ["联网", "Web", "bg-sky-100 text-sky-700"], "地理编码": ["地理", "Geo", "bg-slate-100 text-slate-600"] };

const fmtVal = (v) => (Array.isArray(v) ? v.join(" / ") : typeof v === "number" ? v.toLocaleString() : String(v ?? ""));
const fmtAt = (iso) => { try { return new Date(iso).toLocaleString("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function Badge({ map, k, zh, testid }) {
  const m = map[k] || map.low || Object.values(map)[0];
  return <span data-testid={testid} className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${m[2]}`}>{zh ? m[0] : m[1]}</span>;
}

export function IntakeResearchPanel({ research, open, onToggle, zh }) {
  if (!research) return null;
  const items = research.items || [];
  const applied = new Set(research.applied || []);
  const skipped = new Set(research.skipped || []);
  const A = research.assessor;
  const cites = research.web?.citations || [];
  const notes = research.web?.data?.notes;
  return (
    <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-3 space-y-2" data-testid="intake-research-panel" data-count={items.length}>
      <button type="button" onClick={onToggle} data-testid="intake-research-toggle" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 font-semibold text-sky-700">
          <Globe className="h-3.5 w-3.5" />
          {zh ? `地址联网检索 · ${items.filter((i) => !i.info).length} 项候选` : `Address research · ${items.filter((i) => !i.info).length} candidates`}
          <span className="font-normal text-slate-400">· {(research.sources || []).join(" / ") || (zh ? "无来源" : "no source")}</span>
        </span>
        <span className="flex items-center gap-1 text-slate-400">{fmtAt(research.at)}{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</span>
      </button>
      {open && (
        <>
          <p className="text-[11px] text-slate-500" data-testid="intake-research-status">
            <Search className="inline h-3 w-3 mr-1 -mt-0.5" />{research.address}
            {" · "}{zh ? "联网结果只填入空白字段，已有值不覆盖；保存前请逐项核对来源。" : "Web results fill blank fields only; verify each source before saving."}
            {research.model && <span className="text-slate-400"> · {research.model}</span>}
            {research.skippedSources?.length > 0 && <span className="text-amber-600" data-testid="intake-research-skipped"> · {zh ? "已关闭来源" : "disabled"}: {research.skippedSources.join(" / ")}</span>}
          </p>
          {research.errors?.length > 0 && (
            <p className="text-[11px] text-amber-700 flex items-start gap-1" data-testid="intake-research-errors"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{zh ? "部分来源失败：" : "Some sources failed: "}{research.errors.join("；")}</p>
          )}
          {A && (
            <p className="text-[11px] text-indigo-700 flex items-center gap-1 flex-wrap" data-testid="intake-research-assessor">
              <Landmark className="h-3 w-3" />{zh ? "评估官记录" : "Assessor record"} · AIN {A.ain} · {A.useType}{A.units > 1 ? ` × ${A.units}` : ""} · {A.situs}
              {A.matchCount > 1 && <span className="text-amber-600">（{zh ? `匹配 ${A.matchCount} 条，取第 1 条` : `${A.matchCount} matches, first used`}）</span>}
              {A.url && <a href={A.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline" data-testid="intake-research-assessor-link"><ExternalLink className="h-3 w-3" />{zh ? "查看" : "Open"}</a>}
            </p>
          )}
          {items.length === 0 && <p className="text-[11px] text-slate-400" data-testid="intake-research-empty">{zh ? "未检索到可填入的公开资料" : "No public data found to fill"}</p>}
          <div className="divide-y divide-sky-100">
            {items.map((it) => {
              const state = it.info ? "info" : applied.has(it.key) ? "applied" : skipped.has(it.key) ? "skipped" : "";
              return (
                <div key={it.key} className="flex-wrap md:flex-nowrap flex items-start gap-2 py-1.5 text-xs" data-testid={`intake-research-item-${it.key}`} data-state={state}>
                  <span className="w-28 shrink-0 text-slate-500">{it.label}</span>
                  <span className="flex-1 min-w-0 break-words">
                    <span className="font-medium text-slate-800" data-testid={`intake-research-value-${it.key}`}>{it.value == null ? (zh ? "（仅参考）" : "(reference)") : fmtVal(it.value)}</span>
                    {it.raw && <span className="text-slate-400 ml-1">({it.raw})</span>}
                  </span>
                  <Badge map={CONF} k={it.confidence} zh={zh} testid={`intake-research-conf-${it.key}`} />
                  <Badge map={SRC} k={it.source} zh={zh} testid={`intake-research-src-${it.key}`} />
                  {it.sourceUrl ? (
                    <a href={it.sourceUrl} target="_blank" rel="noreferrer" title={it.sourceUrl} className="text-sky-600 hover:text-sky-800" data-testid={`intake-research-link-${it.key}`}><ExternalLink className="h-3.5 w-3.5" /></a>
                  ) : <span className="w-3.5" />}
                  <span className="w-14 text-right whitespace-nowrap text-[10px]">
                    {state === "applied" && <span className="text-emerald-600 inline-flex items-center gap-0.5"><Check className="h-3 w-3" />{zh ? "已填入" : "filled"}</span>}
                    {state === "skipped" && <span className="text-slate-400 inline-flex items-center gap-0.5"><MinusCircle className="h-3 w-3" />{zh ? "未覆盖" : "kept"}</span>}
                  </span>
                </div>
              );
            })}
          </div>
          {notes && <p className="text-[11px] text-slate-600 bg-white/70 rounded-lg px-2 py-1.5" data-testid="intake-research-notes">{zh ? "联网备注：" : "Notes: "}{notes}</p>}
          {cites.length > 0 && (
            <div className="text-[11px] text-slate-500" data-testid="intake-research-citations">
              <span className="font-medium">{zh ? "来源" : "Sources"}：</span>
              {cites.slice(0, 8).map((c, i) => (
                <a key={c.url + i} href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 mr-2 underline decoration-dotted hover:text-sky-700" data-testid={`intake-research-cite-${i}`}>
                  <ExternalLink className="h-3 w-3" />{(c.title || c.url).slice(0, 40)}
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
